// The card-effect registry: what each Fortress card DOES, keyed by catalogue
// card id.
//
// Behavior lives in CODE, not in the DB catalogue: the versioned `fortress_cards`
// documents own presentation, patterns, costs and win timing (balance-tunable),
// while this table owns semantics. A card id with no entry here is simply
// inert — it can be implemented for its pattern but carries no effect, which is
// exactly how every card behaved before the registry existed.
//
// Nothing effect-shaped is ever stored on game state. `FortressCardState`
// carries only the card id plus its mutable counters (tokens / disabled /
// nodeIds); everything else — granted actions, passive rule changes, caps — is
// derived on demand from `player.fortressCards` × this registry, on both the
// server (validation) and the client (availability UI). The player's sparse
// config overrides (`Player.buildings` / `.vigorZones`) are a materialized
// view of the `passives` listed here, rebuilt from scratch whenever the
// implemented-card set changes (see server `rebuildPlayerOverrides`).
//
// This registry currently covers the effects the engine can express today:
// passive config modifiers and granted special actions, plus per-card token
// caps and round resets. Cards whose effects need mechanisms that don't exist
// yet (lifecycle hooks, win predicates, build gates, payment substitution,
// riders, reaction prompts) gain their entries as those mechanisms land.
/** Vigor-zone level bounds ("energy level" in card text): 0 = forest … 4 = summit. */
export const ZONE_LOWEST = 0;
export const ZONE_HIGHEST = 4;
/** Sums a resource index across banks. */
const sumAt = (banks, i) => banks.reduce((s, b) => s + (b[i] ?? 0), 0);
/** "Win game if 4 artifacts are built by you." Shared by the five artifacts. */
const artifactWin = {
    timing: "immediate",
    met: (q) => q.countTagged("artifact") >= 4,
};
export const CARD_EFFECTS = {
    // --- Win (victory) deck ---------------------------------------------------
    // Token-counter wins: hooks (server effects/hooks.ts) move the tokens; the
    // threshold here ends the game. Timings beyond token thresholds follow the
    // card's printed badge (EOR / SOT), pinned HERE (behavior lives in code —
    // the catalogue's `winTiming` is display data).
    "enchanted-mines": {
        winAtTokens: 7,
        resetsEachRound: { tokens: true }, // "removed at the start of each round"
    },
    vacmakd: { winAtTokens: 8 },
    "giant-sphincter": { winAtTokens: 3 },
    "bursted-flap": { winAtTokens: 3 },
    "penguin-puncher": { winAtTokens: 2 },
    // "Buildings in this fortress may be destroyed. If destroyed, this fortress
    // is placed back into the pile. As long as this fortress is intact, add a
    // token at the end of the round."
    "obsidian-blimp-port": {
        winAtTokens: 2,
        destructible: { returnToPile: true },
        placement: { requireBuildingInZone: ZONE_HIGHEST },
    },
    // "This must be built entirely in the lowest energy zone. Once per round,
    // you may move this fortress by your barracks attack range... Once
    // completely in the highest energy zone, you win. This fortress may be
    // destroyed."
    "traveling-booba": {
        grants: ["move_fortress"],
        placement: { zone: { max: ZONE_LOWEST } },
        resetsEachRound: { disabled: true }, // the once-per-round move re-arms
        destructible: {},
        win: {
            timing: "start-of-turn",
            met: (q) => q.cardNodesAllInZone(ZONE_HIGHEST),
        },
    },
    // "Win the game if you build a mine on a starting node in the starting tile
    // opposite to your own starting tile."
    "mirror-morse": {
        win: { timing: "end-of-round", met: (q) => q.hasOwnMineOppositeHome() },
    },
    // Win-on-implement cards: the gate / placement constraint carries the real
    // difficulty; the win itself fires the moment the fortress stands.
    "piercing-needle": {
        placement: { zone: { min: ZONE_HIGHEST } },
        win: { timing: "immediate", met: () => true },
    },
    "miner-bots": {
        canImplement: (q) => q.hasMineInEveryZone() ? null : "Needs one of your Mines in every zone.",
        win: { timing: "immediate", met: () => true },
    },
    "obsidian-pipeline-masterworks": {
        canImplement: (q) => q.fortressCount >= 6 ? null : "Needs 6 or more fortresses under your control.",
        win: { timing: "immediate", met: () => true },
    },
    // "Obliterate. May be built using other player's buildings." The matcher
    // accepts anyone's free buildings; the win fires the moment it stands.
    "exodial-the-forsaken-one": {
        placement: { useOpponentBuildings: true },
        win: { timing: "immediate", met: () => true },
    },
    // Board-predicate wins.
    "parasitic-breakfast": {
        win: {
            timing: "start-of-turn",
            met: (q) => q.maxOpponentFortressesInAttackRange() >= 3,
        },
    },
    "cg-builder-of-bridges": {
        // Bridge builds change connectivity mid-turn — immediate, checked at every
        // action seam.
        win: { timing: "immediate", met: (q) => q.isAnyZoneFullyConnected() },
    },
    "big-sprawler": {
        // "Occupy 80/X tiles, where X equals the number of players, rounded up."
        win: {
            timing: "start-of-turn",
            met: (q) => q.occupiedTilesConnectedToHome() >=
                Math.ceil(80 / Math.max(1, q.playerCount)),
        },
    },
    "underhand-skyscraper": {
        canImplement: (q) => q.allOwnBuildingsInZone(ZONE_HIGHEST)
            ? null
            : "All your buildings must be in the highest zone.",
        win: {
            timing: "end-of-round",
            met: (q) => q.hasOwnBuildingInZone("barracks", ZONE_HIGHEST),
        },
    },
    // Economy predicates.
    bankman: {
        win: {
            timing: "start-of-turn",
            met: (q) => q.bank[0] >= 9 && q.bank[1] >= 9 && q.bank[2] >= 9,
        },
    },
    "plink-harvester": {
        win: {
            timing: "end-of-round",
            met: (q) => q.production[3] >= 9 && q.bank[3] >= 9,
        },
    },
    "booba-farmer": {
        win: {
            timing: "start-of-turn",
            met: (q) => q.bank.some((have, i) => have > sumAt(q.othersBanks, i)),
        },
    },
    // "Win condition fortresses cost 5 Obsidian less for you (minimum 1)."
    "inverse-molehill": {
        fortressDiscount: { deck: "win", obsidian: 5, minObsidian: 1 },
    },
    // The artifact set: gain a resource on implement (hooks), win at 4 built.
    // -atrocity's "gain 1 of any resource" travels on the implement choice.
    "artifact-of-speed": { tags: ["artifact"], win: artifactWin },
    "artifact-of-courage": { tags: ["artifact"], win: artifactWin },
    "artifact-of-knowledge": { tags: ["artifact"], win: artifactWin },
    "artifact-of-plebians": { tags: ["artifact"], win: artifactWin },
    "artifact-of-atrocity": {
        tags: ["artifact"],
        win: artifactWin,
        implementChoice: "resource", // "Gain 1 of any resource" — picked on implement
    },
    // --- Regular (effect) deck ----------------------------------------------
    // Uses are counted UP as tokens ("place a token... may not exceed 2"), so
    // the cap gates usability; tokens clear each round per the card.
    "godspeed-machine": {
        grants: ["make_scout_indestructible"],
        tokenCap: 2,
        resetsEachRound: { tokens: true },
    },
    "bridgette-the-beauty": { passives: ["bridge_any_resource"] },
    // "When gaining one-time resources, you may double the amount you receive.
    // Place a token every use; max 5." A construct-action rider.
    "energized-bun-bun": { rider: "double_one_time", tokenCap: 5 },
    // "If a player builds within your defence range to this fortress, gain 2
    // resources of any kind." The trigger banks a claim; this action cashes it.
    icarlie: { grants: ["claim_resources"] },
    // Swole Turtle's +1/+2 on one-time energy collection is derived at the
    // collection site by card presence — no registry data needed.
    "hole-worm": {
        grants: ["replicate_fortress"],
        placement: { sameZone: true },
    }, // flips (disabled) on use
    "skippity-star": { grants: ["double_banked_resources"] }, // once per game
    // Round income while lowest mineral production is a lifecycle hook.
    "resource-sequencer": { passives: ["mines_cheaper"] },
    "conversion-treadmill": { passives: ["obsidian_wildcard"] },
    "plasmamagnetic-waves": { passives: ["penalty_reduced"] },
    "kinetic-absorption-facility": {
        grants: ["pay_penalty_with_token"],
        tokenCap: 4,
    },
    "transmutation-bench": {
        passives: ["food_energy_interchange"],
        // "Cannot be built in the lowest energy level."
        placement: { zone: { min: ZONE_LOWEST + 1 } },
    },
    // "You may untap 2 barracks per round": tokens count barracks untapped via
    // this card this round — capped at 2, cleared at round start.
    "telescopic-arm": {
        passives: ["attack_range"],
        grants: ["untap_barracks"],
        tokenCap: 2,
        resetsEachRound: { tokens: true },
    },
    "porkypine-neck": {
        passives: ["defend_range"],
        grants: ["untap_barracks"],
        tokenCap: 2,
        resetsEachRound: { tokens: true },
    },
    "supreme-bondage": { grants: ["set_fortress_lock"] },
    cyclopz: { grants: ["free_cycle_market"], tokenCap: 3 },
    "curious-clit": { grants: ["reorder_fortress_deck"], tokenCap: 3 },
    // The room derives 3-for-everyone / 4-for-owner turn allowances live.
    "action-blaster": { grants: ["buy_extra_action"] },
    "times-up": { grants: ["force_end_round"], tokenCap: 2 },
    "to-the-moon-tokenizer-9000": {
        grants: ["adjust_fortress_token"],
        resetsEachRound: { disabled: true },
    },
    "trifecta-sphinx": { passives: ["building_range"] },
    "rabid-shoes": { passives: ["scout_bridge_range"] },
};
/** The effect bundle for a catalogue card id, or undefined for inert cards. */
export function effectFor(cardId) {
    return CARD_EFFECTS[cardId];
}
/** The card's printed token cap, or +Infinity when it has none. */
export function tokenCapOf(cardId) {
    return CARD_EFFECTS[cardId]?.tokenCap ?? Number.POSITIVE_INFINITY;
}
/** The number worth showing beside a card's token count: its win threshold
 * ("win at N tokens") when it has one, else its usage cap, else +Infinity. */
export function tokenTargetOf(cardId) {
    const fx = CARD_EFFECTS[cardId];
    return fx?.winAtTokens ?? fx?.tokenCap ?? Number.POSITIVE_INFINITY;
}
/**
 * Every special action the player's implemented cards grant right now. A
 * flipped (disabled) card grants nothing until it flips back. Derived — this
 * replaces the old stored `Player.actions` list.
 */
export function grantedActions(cards) {
    const kinds = new Set();
    for (const fc of cards) {
        if (fc.disabled)
            continue;
        for (const kind of CARD_EFFECTS[fc.cardId]?.grants ?? [])
            kinds.add(kind);
    }
    return [...kinds];
}
/**
 * Can the player use `kind` via the specific card `cardId` right now? The one
 * gate both the server (dispatch validation) and the client (Use-button
 * availability) ask. Checks ownership, the grant itself, exhaustion, and the
 * token cap; handlers still enforce their own action-specific rules (targets,
 * costs, turn order).
 */
export function canUseCardAction(cards, cardId, kind) {
    let card;
    for (const fc of cards) {
        if (fc.cardId === cardId) {
            card = fc;
            break;
        }
    }
    if (!card) {
        return { allowed: false, reason: "You don't hold that fortress." };
    }
    if (!CARD_EFFECTS[cardId]?.grants?.includes(kind)) {
        return { allowed: false, reason: "That fortress doesn't grant this action." };
    }
    if (card.disabled) {
        return { allowed: false, reason: "That fortress is exhausted." };
    }
    const cap = tokenCapOf(cardId);
    if (card.tokens >= cap) {
        return { allowed: false, reason: "No uses left — the card is at its token cap." };
    }
    return { allowed: true };
}
//# sourceMappingURL=registry.js.map